import ExcelJS from 'exceljs';
import { Chart as ChartJS } from 'chart.js';
import html2canvas from 'html2canvas';
import { parseMsrObservationSegments } from './msrObservationParser';
import type { MsrCountRow, MsrReportData } from './msrReportTypes';

export type MsrSectionKey = 'team-ticket' | 'resolution-priority' | 'ci-sla';

export const MSR_SECTION_ORDER: MsrSectionKey[] = ['team-ticket', 'resolution-priority', 'ci-sla'];

export interface MsrChartExportTarget {
  id: string;
  label: string;
}

export const MSR_SECTION_CONFIG: Record<
  MsrSectionKey,
  { slideId: string; title: string; fileSlug: string; charts: [MsrChartExportTarget, MsrChartExportTarget] }
> = {
  'team-ticket': {
    slideId: 'msr-slide-team-ticket',
    title: 'Team & Ticket Type Wise Distribution',
    fileSlug: 'Team_Ticket_Type',
    charts: [
      { id: 'msr-chart-team-ticket-team', label: 'Team Wise Ticket Data' },
      { id: 'msr-chart-team-ticket-category', label: 'Category Wise Ticket Data' },
    ],
  },
  'resolution-priority': {
    slideId: 'msr-slide-resolution-priority',
    title: 'Ticket Resolution & Priority Wise Distribution',
    fileSlug: 'Resolution_Priority',
    charts: [
      { id: 'msr-chart-resolution-bins', label: 'Resolution Time Binned Wise Ticket Data' },
      { id: 'msr-chart-resolution-priority', label: 'Priority Wise Ticket Data' },
    ],
  },
  'ci-sla': {
    slideId: 'msr-slide-ci-sla',
    title: 'Top CI Wise Tickets Distribution & Monthly SLA Achievement',
    fileSlug: 'CI_SLA',
    charts: [
      { id: 'msr-chart-ci-treemap', label: 'CI Wise Ticket Data' },
      { id: 'msr-chart-ci-sla', label: 'Ticket SLA Status' },
    ],
  },
};

const MSR_BLUE = 'FF3465A4';
const TABLE_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFF00' },
};
const OBSERVATION_HEADER_FILL: ExcelJS.Fill = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: MSR_BLUE },
};
const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  bottom: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  left: { style: 'thin', color: { argb: 'FFB4B4B4' } },
  right: { style: 'thin', color: { argb: 'FFB4B4B4' } },
};

function restoreChartsInElement(el: HTMLElement): void {
  for (const canvas of el.querySelectorAll('canvas')) {
    const chart = ChartJS.getChart(canvas);
    if (chart) {
      chart.resize();
      chart.update();
    }
  }
}

function chartLooksReady(el: HTMLElement): boolean {
  const canvas = el.querySelector('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) return false;
  if (canvas.width < 2 || canvas.height < 2) return false;
  return ChartJS.getChart(canvas) != null;
}

function elementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 2 && rect.height > 2;
}

async function waitForChartReady(el: HTMLElement, maxMs = 1200): Promise<void> {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    if (chartLooksReady(el)) {
      await new Promise((resolve) => setTimeout(resolve, 80));
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, 80));
  }
}

function copyCanvasBitmapToClone(sourceEl: HTMLElement, cloneEl: HTMLElement): void {
  const origCanvases = sourceEl.querySelectorAll('canvas');
  const cloneCanvases = cloneEl.querySelectorAll('canvas');
  origCanvases.forEach((orig, i) => {
    const cloned = cloneCanvases[i];
    if (!(orig instanceof HTMLCanvasElement) || !(cloned instanceof HTMLCanvasElement)) return;
    if (orig.width < 1 || orig.height < 1) return;
    const ctx = cloned.getContext('2d');
    if (ctx) ctx.drawImage(orig, 0, 0);
  });
}

async function captureTreemapPanel(el: HTMLElement, rect: DOMRect): Promise<{
  dataUrl: string;
  aspectRatio: number;
  base64: string;
} | null> {
  const canvas = el.querySelector('canvas');
  if (!(canvas instanceof HTMLCanvasElement)) return null;
  const chart = ChartJS.getChart(canvas);
  if (!chart || !chartLooksReady(el)) return null;

  const chartDataUrl = chart.toBase64Image('image/png', 2);
  const scale = 2;
  const width = Math.max(Math.round(rect.width * scale), 400);
  const height = Math.max(Math.round(rect.height * scale), 360);
  const offscreen = document.createElement('canvas');
  offscreen.width = width;
  offscreen.height = height;
  const ctx = offscreen.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

  const titleText = el.querySelector('h3')?.textContent?.trim() ?? '';
  let chartTop = 12 * scale;
  if (titleText) {
    ctx.fillStyle = '#1e293b';
    ctx.font = `600 ${14 * scale}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(titleText, width / 2, 12 * scale);
    chartTop = 46 * scale;
  }

  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('treemap image load failed'));
      img.src = chartDataUrl;
    });
  } catch {
    return null;
  }

  const chartHeight = height - chartTop - 10 * scale;
  ctx.drawImage(img, 8 * scale, chartTop, width - 16 * scale, chartHeight);

  const dataUrl = offscreen.toDataURL('image/png');
  const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1]! : dataUrl;
  return { dataUrl, base64, aspectRatio: width / height };
}

export async function captureMsrElement(
  elementId: string
): Promise<{ dataUrl: string; aspectRatio: number; base64: string } | null> {
  const el = document.getElementById(elementId);
  if (!el) return null;
  try {
    if (!elementInViewport(el)) {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
    await waitForChartReady(el);
    const rect = el.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return null;

    const isTreemap = el.querySelector('[data-msr-treemap]') != null;
    if (isTreemap) {
      const treemapCapture = await captureTreemapPanel(el, rect);
      if (treemapCapture) return treemapCapture;
    }

    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true,
      onclone: (_doc, clone) => {
        copyCanvasBitmapToClone(el, clone);
      },
    });
    const dataUrl = canvas.toDataURL('image/png');
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1]! : dataUrl;
    return {
      dataUrl,
      base64,
      aspectRatio: rect.width / Math.max(rect.height, 1),
    };
  } catch {
    return null;
  } finally {
    restoreChartsInElement(el);
  }
}

/** Re-paint live Chart.js canvases after a section export (avoids black treemap after capture). */
export function restoreMsrSectionCharts(section: MsrSectionKey): void {
  const config = MSR_SECTION_CONFIG[section];
  for (const chart of config.charts) {
    const el = document.getElementById(chart.id);
    if (el) restoreChartsInElement(el);
  }
}

/** Re-paint every MSR chart — needed because scrolling during one section export can blank off-screen treemaps. */
export function restoreAllMsrCharts(): void {
  const restore = (): void => {
    for (const section of MSR_SECTION_ORDER) {
      restoreMsrSectionCharts(section);
    }
  };
  restore();
  requestAnimationFrame(restore);
}

/** Preserve scroll position and restore all MSR charts after capture-based export work. */
export async function withMsrExportCapture<T>(work: () => Promise<T>): Promise<T> {
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  try {
    return await work();
  } finally {
    window.scrollTo(scrollX, scrollY);
    restoreAllMsrCharts();
  }
}

/** Full slide capture — used for PDF export only. */
export async function captureMsrSlide(
  elementId: string
): Promise<{ dataUrl: string; aspectRatio: number; base64: string } | null> {
  return captureMsrElement(elementId);
}

export function msrSectionFilename(
  sectionSlug: string,
  customerName: string,
  dateFrom: string,
  dateTo: string,
  ext: 'pdf' | 'xlsx'
): string {
  const safe = customerName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'customer';
  const from = dateFrom.slice(0, 10) || 'unknown';
  const to = dateTo.slice(0, 10) || 'unknown';
  return `MSR_${sectionSlug}_${safe}_from_${from}_to_${to}.${ext}`;
}

function styleTableHeader(cell: ExcelJS.Cell): void {
  cell.font = { bold: true };
  cell.fill = TABLE_HEADER_FILL;
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  cell.border = THIN_BORDER;
}

function styleTableBodyCell(cell: ExcelJS.Cell, alignRight = false): void {
  cell.alignment = { horizontal: alignRight ? 'right' : 'left', vertical: 'top', wrapText: true };
  cell.border = THIN_BORDER;
}

function writeCountTableAtColumn(
  ws: ExcelJS.Worksheet,
  startRow: number,
  startCol: number,
  title: string,
  col1Label: string,
  col2Label: string,
  rows: MsrCountRow[]
): number {
  let row = startRow;
  ws.getCell(row, startCol).value = title;
  ws.getCell(row, startCol).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  row += 1;

  styleTableHeader(ws.getCell(row, startCol));
  ws.getCell(row, startCol).value = col1Label;
  styleTableHeader(ws.getCell(row, startCol + 1));
  ws.getCell(row, startCol + 1).value = col2Label;
  row += 1;

  if (rows.length === 0) {
    ws.getCell(row, startCol).value = 'No data';
    ws.mergeCells(row, startCol, row, startCol + 1);
    styleTableBodyCell(ws.getCell(row, startCol));
    return row + 1;
  }

  for (const item of rows) {
    ws.getCell(row, startCol).value = item.label;
    ws.getCell(row, startCol + 1).value = item.count;
    styleTableBodyCell(ws.getCell(row, startCol));
    styleTableBodyCell(ws.getCell(row, startCol + 1), true);
    row += 1;
  }

  return row;
}

/** Left table in cols A–B, right table in cols D–E (gap in column C). */
function writeSideBySideCountTables(
  ws: ExcelJS.Worksheet,
  startRow: number,
  left: { title: string; col1: string; col2: string; rows: MsrCountRow[] },
  right: { title: string; col1: string; col2: string; rows: MsrCountRow[] }
): number {
  const leftEnd = writeCountTableAtColumn(
    ws,
    startRow,
    1,
    left.title,
    left.col1,
    left.col2,
    left.rows
  );
  const rightEnd = writeCountTableAtColumn(
    ws,
    startRow,
    4,
    right.title,
    right.col1,
    right.col2,
    right.rows
  );
  return Math.max(leftEnd, rightEnd) + 1;
}

function writeObservations(ws: ExcelJS.Worksheet, startRow: number, observations: string[]): number {
  if (observations.length === 0) return startRow;

  let row = startRow;
  const header = ws.getCell(row, 1);
  header.value = 'Observation';
  header.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
  header.fill = OBSERVATION_HEADER_FILL;
  header.alignment = { horizontal: 'left', vertical: 'middle' };
  ws.mergeCells(row, 1, row, 5);
  row += 1;

  for (const obs of observations) {
    const cell = ws.getCell(row, 1);
    const segments = parseMsrObservationSegments(obs);
    cell.value = {
      richText: segments.map((seg) => ({
        text: seg.text,
        font: { bold: seg.bold, size: 10, color: { argb: 'FF334155' } },
      })),
    };
    cell.alignment = { wrapText: true, vertical: 'top' };
    cell.border = THIN_BORDER;
    ws.mergeCells(row, 1, row, 5);
    ws.getRow(row).height = Math.max(30, Math.ceil(obs.length / 80) * 15);
    row += 1;
  }

  return row + 1;
}

const CHART_IMAGE_WIDTH = 400;
const CHART_IMAGE_ROW_HEIGHT = 18;

async function writeChartImages(
  ws: ExcelJS.Worksheet,
  workbook: ExcelJS.Workbook,
  charts: [MsrChartExportTarget, MsrChartExportTarget],
  startRow: number
): Promise<number> {
  let row = startRow;
  const captures = await Promise.all(
    charts.map(async (chart) => ({
      chart,
      capture: await captureMsrElement(chart.id),
    }))
  );

  ws.getCell(row, 1).value = 'Charts';
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  row += 1;

  ws.getCell(row, 1).value = captures[0]!.chart.label;
  ws.getCell(row, 1).font = { bold: true, size: 11 };
  ws.getCell(row, 4).value = captures[1]!.chart.label;
  ws.getCell(row, 4).font = { bold: true, size: 11 };
  const imageStartRow = row + 1;

  let maxImageHeight = 0;
  let anyCaptured = false;

  captures.forEach((item, index) => {
    if (!item.capture?.base64) return;
    anyCaptured = true;
    const imageHeight = Math.max(200, Math.round(CHART_IMAGE_WIDTH / item.capture.aspectRatio));
    maxImageHeight = Math.max(maxImageHeight, imageHeight);

    const imageId = workbook.addImage({
      base64: item.capture.base64,
      extension: 'png',
    });

    ws.addImage(imageId, {
      tl: { col: index === 0 ? 0 : 3.2, row: imageStartRow - 1 },
      ext: { width: CHART_IMAGE_WIDTH, height: imageHeight },
    });
  });

  if (!anyCaptured) {
    ws.getCell(imageStartRow, 1).value =
      'Chart capture unavailable. Ensure this section is visible on screen and try again.';
    ws.mergeCells(imageStartRow, 1, imageStartRow, 5);
    return imageStartRow + 2;
  }

  const rowsForImages = Math.ceil(maxImageHeight / CHART_IMAGE_ROW_HEIGHT) + 1;
  for (let i = 0; i < rowsForImages; i++) {
    ws.getRow(imageStartRow + i).height = CHART_IMAGE_ROW_HEIGHT;
  }

  return imageStartRow + rowsForImages + 1;
}

function writeSectionData(
  ws: ExcelJS.Worksheet,
  section: MsrSectionKey,
  data: MsrReportData,
  startRow: number
): number {
  const { teamTicketDistribution, resolutionPriorityDistribution, ciSlaDistribution } = data;

  ws.getCell(startRow, 1).value = 'Data';
  ws.getCell(startRow, 1).font = { bold: true, size: 12, color: { argb: MSR_BLUE } };
  const dataRow = startRow + 1;

  let row: number;
  if (section === 'team-ticket') {
    row = writeSideBySideCountTables(
      ws,
      dataRow,
      {
        title: 'Team Wise Ticket Data',
        col1: 'Team',
        col2: 'Count',
        rows: teamTicketDistribution.teamWise,
      },
      {
        title: 'Category Wise Ticket Data',
        col1: 'Category',
        col2: 'Count',
        rows: teamTicketDistribution.ticketTypeWise,
      }
    );
    row = writeObservations(ws, row, teamTicketDistribution.observations);
  } else if (section === 'resolution-priority') {
    row = writeSideBySideCountTables(
      ws,
      dataRow,
      {
        title: 'Resolution Time Binned Wise Ticket Data',
        col1: 'Resolution Time',
        col2: 'Count',
        rows: resolutionPriorityDistribution.resolutionBins,
      },
      {
        title: 'Priority Wise Ticket Data',
        col1: 'Priority',
        col2: 'Count',
        rows: resolutionPriorityDistribution.priorityWise,
      }
    );
    row = writeObservations(ws, row, resolutionPriorityDistribution.observations);
  } else {
    row = writeSideBySideCountTables(
      ws,
      dataRow,
      {
        title: 'CI Wise Ticket Data',
        col1: 'CI',
        col2: 'Count',
        rows: ciSlaDistribution.ciWise,
      },
      {
        title: 'Ticket SLA Status',
        col1: 'Status',
        col2: 'Count',
        rows: [
          { label: 'Achieved', count: ciSlaDistribution.slaStatus.achieved },
          { label: 'Breached', count: ciSlaDistribution.slaStatus.breached },
        ],
      }
    );
    row = writeObservations(ws, row, ciSlaDistribution.observations);
  }

  return row;
}

export type MsrSectionExport = { blob: Blob; fileName: string };

export async function exportMsrSectionExcel(
  section: MsrSectionKey,
  data: MsrReportData,
  dateFrom: string,
  dateTo: string
): Promise<MsrSectionExport> {
  return withMsrExportCapture(async () => {
    const config = MSR_SECTION_CONFIG[section];

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Pulse';
    workbook.created = new Date();

    const ws = workbook.addWorksheet('Report', {
      views: [{ showGridLines: true }],
    });

    ws.getColumn(1).width = 36;
    ws.getColumn(2).width = 14;
    ws.getColumn(3).width = 4;
    ws.getColumn(4).width = 36;
    ws.getColumn(5).width = 14;

    let row = 1;
    ws.getCell(row, 1).value = data.customerName;
    ws.getCell(row, 1).font = { bold: true, size: 16, color: { argb: MSR_BLUE } };
    ws.mergeCells(row, 1, row, 5);
    row += 1;

    ws.getCell(row, 1).value = config.title;
    ws.getCell(row, 1).font = { bold: true, size: 11, color: { argb: 'FF475569' } };
    ws.mergeCells(row, 1, row, 5);
    row += 2;

    ws.getCell(row, 1).value = 'Period';
    ws.getCell(row, 1).font = { bold: true };
    ws.getCell(row, 2).value = `${dateFrom.slice(0, 10)} to ${dateTo.slice(0, 10)}`;
    row += 2;

    row = await writeChartImages(ws, workbook, config.charts, row);
    writeSectionData(ws, section, data, row);

    const buffer = await workbook.xlsx.writeBuffer();
    return {
      blob: new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      fileName: msrSectionFilename(config.fileSlug, data.customerName, dateFrom, dateTo, 'xlsx'),
    };
  });
}
